<template>
    <div class="navigator-chart-wrapper">
        <div v-if="title" class="navigator-chart-header">
            <div class="navigator-chart-title">{{ title }}</div>
            <div v-if="subtitle" class="navigator-chart-subtitle">{{ subtitle }}</div>
        </div>
        <div class="navigator-chart-canvas">
            <canvas ref="chartCanvas"></canvas>
        </div>
    </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { Chart, registerables } from 'chart.js';

Chart.register(...registerables);

export default {
    name: 'NavigatorChart',
    props: {
        type: { type: String, default: 'line' },
        title: { type: String, default: '' },
        subtitle: { type: String, default: '' },
        labels: { type: Array, default: () => [] },
        datasets: { type: Array, default: () => [] },
        data: { type: Array, default: () => [] },
        colors: { type: Array, default: () => [] },
    },
    setup(props) {
        const chartCanvas = ref(null);
        let chartInstance = null;

        const defaultColors = ['#8b5cf6', '#ec4899', '#10f7cf', '#f59e0b', '#3b82f6', '#22c55e', '#ef4444', '#6366f1'];

        function isDarkMode() {
            return document.body.classList.contains('dark-mode');
        }

        function getThemeColors() {
            const dark = isDarkMode();
            return {
                gridColor: dark ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.06)',
                tickColor: dark ? '#6b7280' : '#9ca3af',
                tooltipBg: dark ? 'rgba(15, 15, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                tooltipTitle: dark ? '#f3f4f6' : '#1f2937',
                tooltipBody: dark ? '#d1d5db' : '#4b5563',
                tooltipBorder: dark ? 'rgba(139, 92, 246, 0.2)' : 'rgba(139, 92, 246, 0.15)',
                legendColor: dark ? '#9ca3af' : '#6b7280',
            };
        }

        function createChart() {
            if (!chartCanvas.value) return;
            if (chartInstance) chartInstance.destroy();

            const ctx = chartCanvas.value.getContext('2d');
            const chartType = props.type === 'stacked-bar' ? 'bar' : props.type;
            const theme = getThemeColors();

            let chartData;

            if (props.type === 'doughnut' && props.data.length > 0) {
                chartData = {
                    labels: props.labels,
                    datasets: [{
                        data: props.data,
                        backgroundColor: props.colors.length > 0 ? props.colors : defaultColors.slice(0, props.data.length),
                        borderColor: 'transparent',
                        borderWidth: 2,
                    }],
                };
            } else {
                chartData = {
                    labels: props.labels,
                    datasets: (props.datasets || []).map((ds, i) => {
                        const color = ds.color || defaultColors[i % defaultColors.length];
                        const base = {
                            label: ds.label || `Series ${i + 1}`,
                            data: ds.data || [],
                            borderColor: color,
                            backgroundColor: props.type === 'line' && ds.fill
                                ? hexToRgba(color, 0.1)
                                : (props.type === 'bar' || props.type === 'stacked-bar')
                                    ? hexToRgba(color, 0.8)
                                    : color,
                            borderWidth: props.type === 'line' ? 2 : 0,
                            fill: ds.fill || false,
                            tension: 0.3,
                            pointRadius: 0,
                            pointHoverRadius: 4,
                        };
                        if (ds.yAxis === 'right') base.yAxisID = 'y1';
                        return base;
                    }),
                };
            }

            const options = {
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index', intersect: false },
                plugins: {
                    legend: {
                        display: (props.datasets?.length > 1) || props.type === 'doughnut',
                        position: props.type === 'doughnut' ? 'right' : 'top',
                        labels: {
                            color: theme.legendColor,
                            font: { family: "'Nunito', sans-serif", size: 11 },
                            usePointStyle: true,
                            pointStyle: 'circle',
                            boxWidth: 8,
                            padding: 12,
                        },
                    },
                    tooltip: {
                        backgroundColor: theme.tooltipBg,
                        titleColor: theme.tooltipTitle,
                        bodyColor: theme.tooltipBody,
                        titleFont: { family: "'Nunito', sans-serif", weight: '600' },
                        bodyFont: { family: "'JetBrains Mono', monospace", size: 12 },
                        borderColor: theme.tooltipBorder,
                        borderWidth: 1,
                        cornerRadius: 8,
                        padding: 10,
                    },
                },
                scales: {},
            };

            if (props.type !== 'doughnut') {
                options.scales.x = {
                    grid: { color: theme.gridColor },
                    ticks: { color: theme.tickColor, font: { family: "'Nunito', sans-serif", size: 10 }, maxTicksLimit: 8, maxRotation: 0 },
                };
                options.scales.y = {
                    grid: { color: theme.gridColor },
                    ticks: { color: theme.tickColor, font: { family: "'JetBrains Mono', monospace", size: 10 } },
                    beginAtZero: props.type !== 'line',
                };
                if (props.type === 'stacked-bar') {
                    options.scales.x.stacked = true;
                    options.scales.y.stacked = true;
                }
                if (props.datasets?.some(ds => ds.yAxis === 'right')) {
                    options.scales.y1 = {
                        position: 'right',
                        grid: { drawOnChartArea: false },
                        ticks: { color: theme.tickColor, font: { family: "'JetBrains Mono', monospace", size: 10 } },
                    };
                }
            }

            chartInstance = new Chart(ctx, { type: chartType, data: chartData, options });
        }

        function hexToRgba(hex, alpha) {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }

        onMounted(() => createChart());
        onBeforeUnmount(() => { if (chartInstance) chartInstance.destroy(); });
        watch(() => [props.labels, props.datasets, props.data], () => createChart(), { deep: true });

        return { chartCanvas };
    },
};
</script>

<style scoped>
.navigator-chart-wrapper {
    background: var(--nav-chart-bg, rgba(0, 0, 0, 0.2));
    border: 1px solid var(--nav-card-border, rgba(255, 255, 255, 0.06));
    border-radius: var(--compass-radius-sm, 8px);
    padding: 16px;
}

.navigator-chart-header { margin-bottom: 12px; }

.navigator-chart-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--nav-text, #e5e7eb);
}

.navigator-chart-subtitle {
    font-size: 12px;
    color: var(--nav-text-muted, #6b7280);
    margin-top: 2px;
}

.navigator-chart-canvas {
    position: relative;
    height: 260px;
}
</style>
